import type {
  BatchArgs,
  BatchResponse,
  ClonedPageMetrics,
  Playbook,
  Resolution,
  ResourceType,
} from '@carboncopyco/types';
import { removeUndefinedValues } from '@carboncopyco/utils';
import type { AnalyticsActivityResponse } from '../analyticsServiceApi';
import {
  createBaseApi,
  handleBatchRequest,
  serializeMultiParams,
} from '../utils';
import type {
  DemoSessionActivity,
  SessionsAsset,
  SessionsAssetType,
  SessionsOpportunity,
  SessionsPresenter,
  SessionsTour,
  TourActivity,
} from './types';

export type GetSessionsParams = {
  workspaceIds?: string[];
  start?: string;
  end?: string;
  timezone: string;
  internalSessions?: boolean;
  shortSessions?: boolean;
  organizationId: string;
  resourceTypes: ResourceType[];
};

export type GetPaginationSessionsParams = GetSessionsParams & BatchArgs;

export type GetSessionsSummaryArgs = GetSessionsParams & {
  assetType: 'demo' | 'tour';
};

export type GetSessionsSummaryResponse = {
  values: {
    medianDuration: number;
    occurrences: number;
    avgScreensVisited?: number;
    avgBounceRate: number;
    avgCompletionRate: number;
  };
};

export type GetSessionsAnalyticsActivityArgs = GetSessionsParams & {
  demoId?: string;
  tourId?: string;
  playbookId?: string;
  movingAverage?: string;
  resolution: Resolution;
  resourceTypes: ResourceType[];
  userId?: string;
};

export type GetDemoSessionBreakdownArgs = GetPaginationSessionsParams & {
  presenters?: SessionsPresenter['email'][];
  opportunities?: string[];
  demoIds?: string[];
  shareIds?: string[];
  demoId?: string;
  withScreenshots?: boolean;
  playbookId?: string;
};

export type GetSessionsAssetsResponse = {
  asset: SessionsAsset;
  assetType: SessionsAssetType;
  totalVisits: number;
  totalShares: number;
  totalPresented: number;
};

export type GetSharedPresentationsBreakdownArgs = Omit<
  GetPaginationSessionsParams,
  'resourceTypes'
> & {
  opportunities?: string[];
  demoIds?: string[];
  shareIds?: string[];
  demoId?: string;
  withScreenshots?: boolean;
};

export type GetTourSessionsBreakdownArgs = Omit<
  GetPaginationSessionsParams,
  'resourceTypes'
> & {
  tourIds?: string[];
  shareIds?: string[];
  tourId?: string;
};

export type GetSessionOpportunityArgs = GetPaginationSessionsParams & {
  demoId?: string;
  tourId?: string;
  playbookId?: string;
};
export type GetSessionPresentersArgs = GetPaginationSessionsParams & {
  demoId?: string;
  tourId?: string;
  playbookId?: string;
};

export type GetBreakdownCSVResponse = Record<ResourceType, string>;
export type GetSessionsCSVResponse = Record<'signedUrl', string>;

export type GetBreakdownCSVArgs = GetDemoSessionBreakdownArgs &
  GetSharedPresentationsBreakdownArgs &
  GetTourSessionsBreakdownArgs & {
    resourceTypes: ResourceType[];
    playbookId?: Playbook['id'];
  };

export type GetSessionScreenshotsArgs = BatchArgs & {
  organizationId: string;
  workspaceIds: string;
  sessionId: string;
};

export type GetSessionsScreenshots = {
  image: string | null;
  url: string;
  title: string | null;
};

type GetCloneMetricsArgs = {
  organizationId: string;
  recordingId: string;
  workspacesIds: string[];
};

export type CloneMetricsResponse = {
  durationInSeconds: number;
  clicks: number;
  uniqueFullUrls: number;
};

export const [sessionsApi, initSessionsApi] = createBaseApi({
  name: 'sessionsApi',
  tagTypes: ['AnalyticsIndexPage'],
  endpoints: (builder) => ({
    getSessionsAnalyticsActivity: builder.query<
      AnalyticsActivityResponse,
      GetSessionsAnalyticsActivityArgs
    >({
      query: ({
        organizationId,
        demoId,
        tourId,
        start,
        end,
        timezone,
        resolution,
        resourceTypes,
        movingAverage,
        workspaceIds,
        shortSessions,
        internalSessions,
        playbookId,
        userId,
      }) => {
        const multiParams = `?${serializeMultiParams({
          workspaceIds,
          resourceTypes,
        })}`;
        return {
          url: `/api/v1/sessions/activity${multiParams}`,
          params: {
            includeShared: true,
            userId,
            resolution,
            organizationId,
            demoId,
            tourId,
            playbookId,
            start,
            end,
            timezone,
            movingAverage,
            internalSessions: String(internalSessions),
            shortSessions: String(shortSessions),
          },
        };
      },
      providesTags: ['AnalyticsIndexPage'],
    }),
    getSessionsSummary: builder.query<
      GetSessionsSummaryResponse,
      GetSessionsSummaryArgs
    >({
      query: ({
        assetType,
        organizationId,
        start,
        end,
        timezone,
        resourceTypes,
        internalSessions,
        shortSessions,
        workspaceIds,
      }) => ({
        url: `/api/v1/sessions/summary/${assetType}?${serializeMultiParams({
          workspaceIds,
          resourceTypes,
        })}`,
        params: {
          includeShared: true,
          organizationId,
          start,
          end,
          timezone,
          internalSessions: String(internalSessions),
          shortSessions: String(shortSessions),
        },
      }),
    }),
    getSessionsOpportunities: builder.query<
      BatchResponse<SessionsOpportunity>,
      GetSessionOpportunityArgs
    >({
      query: ({
        organizationId,
        start,
        end,
        timezone,
        resourceTypes,
        internalSessions,
        shortSessions,
        workspaceIds,
        demoId,
        tourId,
        playbookId,
        ...batchArgs
      }) =>
        handleBatchRequest({
          ...batchArgs,
          url: `/api/v1/sessions/opportunities?${serializeMultiParams({
            workspaceIds,
            resourceTypes,
          })}`,
          params: {
            includeShared: String(true),
            organizationId,
            start,
            end,
            timezone,
            internalSessions: String(internalSessions),
            shortSessions: String(shortSessions),
            demoId,
            tourId,
            playbookId,
          },
        }),
      serializeQueryArgs: ({ queryArgs }) => {
        const { nextBatchCursor, ...rest } = queryArgs;
        return rest;
      },
      merge: (queryCache, newBatch) => {
        queryCache.metadata = newBatch.metadata;

        if (!newBatch.items.length) {
          if (queryCache.items.length === 1) {
            queryCache.items = [];
          }
          return;
        }

        const exists = newBatch.items.some((item) => {
          return queryCache.items.find(
            (cacheItem) => cacheItem.name === item.name
          );
        });

        queryCache.items = exists
          ? [...newBatch.items]
          : [...queryCache.items, ...newBatch.items];
      },
      forceRefetch({ currentArg, previousArg, endpointState }) {
        return (
          !(endpointState?.status === 'fulfilled') ||
          currentArg.nextBatchCursor !== previousArg.nextBatchCursor
        );
      },
    }),
    getSessionsAssets: builder.query<
      BatchResponse<GetSessionsAssetsResponse>,
      GetPaginationSessionsParams
    >({
      query: ({
        organizationId,
        start,
        end,
        timezone,
        resourceTypes,
        internalSessions,
        shortSessions,
        workspaceIds,
        ...batchArgs
      }) =>
        handleBatchRequest({
          ...batchArgs,
          url: `/api/v1/sessions/assets?${serializeMultiParams({
            workspaceIds,
            resourceTypes,
          })}`,
          params: {
            includeShared: 'true',
            organizationId,
            internalSessions: String(internalSessions),
            shortSessions: String(shortSessions),
            start,
            end,
            timezone,
          },
        }),
      serializeQueryArgs: ({ queryArgs }) => {
        const { nextBatchCursor, ...rest } = queryArgs;
        return rest;
      },
      merge: (queryCache, newBatch) => {
        queryCache.metadata = newBatch.metadata;

        if (!newBatch.items.length) {
          if (queryCache.items.length === 1) {
            queryCache.items = [];
          }
          return;
        }

        const exists = newBatch.items.some((item) => {
          return queryCache.items.find(
            (cacheItem) => cacheItem.asset.assetId === item.asset.assetId
          );
        });

        queryCache.items = exists
          ? [...newBatch.items]
          : [...queryCache.items, ...newBatch.items];
      },
      forceRefetch({ currentArg, previousArg, endpointState }) {
        return (
          !(endpointState?.status === 'fulfilled') ||
          currentArg.nextBatchCursor !== previousArg.nextBatchCursor
        );
      },
    }),
    getSessionsTours: builder.query<
      BatchResponse<SessionsTour>,
      GetPaginationSessionsParams
    >({
      query: ({
        organizationId,
        start,
        end,
        timezone,
        resourceTypes,
        internalSessions,
        shortSessions,
        workspaceIds,
        ...batchArgs
      }) => {
        return handleBatchRequest({
          ...batchArgs,
          url: `/api/v1/sessions/tours?${serializeMultiParams({
            workspaceIds,
            resourceTypes,
          })}`,
          params: {
            includeShared: 'true',
            organizationId,
            start,
            end,
            timezone,
            internalSessions: String(internalSessions),
            shortSessions: String(shortSessions),
          },
        });
      },
      serializeQueryArgs: ({ queryArgs }) => {
        if (!queryArgs) {
          return queryArgs;
        }
        const { nextBatchCursor, ...rest } = queryArgs;
        return rest;
      },
      merge: (queryCache, newBatch) => {
        queryCache.metadata = newBatch.metadata;
        const currentItemsIds = queryCache.items.map(
          (item) => item.asset.assetId
        );

        const newItems = newBatch.items.filter(
          (item) => !currentItemsIds.includes(item.asset.assetId)
        );

        queryCache.items = [...queryCache.items, ...newItems];
      },
      forceRefetch({ currentArg, previousArg, endpointState }) {
        return (
          !(endpointState?.status === 'fulfilled') ||
          currentArg.nextBatchCursor !== previousArg.nextBatchCursor
        );
      },
    }),
    getSessionsPresenters: builder.query<
      BatchResponse<SessionsPresenter>,
      GetSessionPresentersArgs
    >({
      query: ({
        organizationId,
        start,
        end,
        timezone,
        resourceTypes,
        internalSessions,
        shortSessions,
        workspaceIds,
        demoId,
        tourId,
        playbookId,
        ...batchArgs
      }) =>
        handleBatchRequest({
          ...batchArgs,
          url: `/api/v1/sessions/presenters?${serializeMultiParams({
            workspaceIds,
            resourceTypes,
          })}`,
          params: {
            includeShared: 'true',
            organizationId,
            start,
            end,
            timezone,
            internalSessions: String(internalSessions),
            shortSessions: String(shortSessions),
            demoId,
            tourId,
            playbookId,
          },
        }),
      serializeQueryArgs: ({ queryArgs }) => {
        if (!queryArgs) {
          return queryArgs;
        }
        const { nextBatchCursor, ...rest } = queryArgs;
        return rest;
      },
      merge: (queryCache, newBatch) => {
        queryCache.metadata = newBatch.metadata;
        const currentItemsIds = queryCache.items.map((item) => item.email);

        const newItems = newBatch.items.filter(
          (item) => !currentItemsIds.includes(item.email)
        );

        queryCache.items = [...queryCache.items, ...newItems];
      },
      forceRefetch({ currentArg, previousArg, endpointState }) {
        return (
          !(endpointState?.status === 'fulfilled') ||
          currentArg.nextBatchCursor !== previousArg.nextBatchCursor
        );
      },
    }),
    getDemoSessionsBreakdown: builder.query<
      BatchResponse<DemoSessionActivity>,
      GetDemoSessionBreakdownArgs
    >({
      query: ({
        organizationId,
        start,
        end,
        timezone,
        presenters,
        opportunities,
        internalSessions,
        shortSessions,
        workspaceIds,
        demoIds,
        shareIds,
        demoId,
        playbookId,
        withScreenshots,
        resourceTypes,
        ...batchArgs
      }) => {
        const multiParams = serializeMultiParams({
          workspaceIds,
          opportunities,
          demoIds,
          presenters,
          shareIds,
          resourceTypes,
        });
        return handleBatchRequest({
          ...batchArgs,
          url: `/api/v1/sessions/breakdown${multiParams ? `?${multiParams}` : ''}`,
          params: removeUndefinedValues({
            includeShared: true,
            organizationId,
            start,
            end,
            timezone,
            internalSessions: String(internalSessions),
            shortSessions: String(shortSessions),
            demoId,
            playbookId,
            withScreenshots: String(withScreenshots),
          }),
        });
      },
      serializeQueryArgs: ({ queryArgs }) => {
        if (!queryArgs) {
          return queryArgs;
        }
        const { nextBatchCursor, ...rest } = queryArgs;
        return rest;
      },
      merge: (queryCache, newBatch) => {
        queryCache.metadata = newBatch.metadata;
        const currentItemsIds = queryCache.items.map((item) => item.id);

        const newItems = newBatch.items.filter(
          (item) => !currentItemsIds.includes(item.id)
        );

        queryCache.items = [...queryCache.items, ...newItems];
      },
      forceRefetch({ currentArg, previousArg, endpointState }) {
        return (
          !(endpointState?.status === 'fulfilled') ||
          currentArg.nextBatchCursor !== previousArg.nextBatchCursor
        );
      },
    }),
    getTourSessionsBreakdown: builder.query<
      BatchResponse<TourActivity>,
      GetTourSessionsBreakdownArgs
    >({
      query: ({
        organizationId,
        start,
        end,
        timezone,
        internalSessions,
        shortSessions,
        workspaceIds,
        tourIds,
        shareIds,
        tourId,
        ...batchArgs
      }) => {
        const multiParams = serializeMultiParams({
          workspaceIds,
          tourIds,
          shareIds,
        });
        return handleBatchRequest({
          ...batchArgs,
          url: `/api/v1/sessions/breakdown${multiParams ? `?${multiParams}` : ''}`,
          params: {
            includeShared: 'true',
            resourceTypes: 'tour',
            organizationId,
            start,
            end,
            timezone,
            internalSessions: String(internalSessions),
            shortSessions: String(shortSessions),
            tourId,
          },
        });
      },
    }),
    getSessionsCsv: builder.query<GetSessionsCSVResponse, GetBreakdownCSVArgs>({
      query: ({
        organizationId,
        start,
        end,
        timezone,
        internalSessions,
        shortSessions,
        resourceTypes,
        workspaceIds,
        tourIds,
        opportunities,
        presenters,
        shareIds,
        demoIds,
        tourId,
        demoId,
        playbookId,
      }) => {
        const multiParams = serializeMultiParams({
          workspaceIds,
          tourIds,
          shareIds,
          demoIds,
          opportunities,
          presenters,
          resourceTypes,
        });
        return {
          url: `/api/v1/sessions/breakdown/csv${multiParams ? `?${multiParams}` : ''}`,
          params: {
            includeShared: true,
            organizationId,
            start,
            end,
            timezone,
            internalSessions: String(internalSessions),
            shortSessions: String(shortSessions),
            tourId,
            demoId,
            playbookId,
          },
        };
      },
    }),
    getSessionScreenshots: builder.query<
      BatchResponse<GetSessionsScreenshots>,
      GetSessionScreenshotsArgs
    >({
      query: ({ organizationId, sessionId, workspaceIds, ...batchArgs }) =>
        handleBatchRequest({
          ...batchArgs,
          url: `api/v1/sessions/${sessionId}/screens`,
          params: {
            workspaceIds,
            organizationId,
            includeShared: 'true',
          },
        }),
    }),
    getCloneMetrics: builder.query<CloneMetricsResponse, GetCloneMetricsArgs>({
      query: ({ organizationId, recordingId }) => ({
        url: '/api/v1/sessions/summary/record/metrics',
        method: 'GET',
        params: {
          organization_id: organizationId,
          record_id: recordingId,
        },
      }),
    }),

    getClonePagesMetrics: builder.query<
      ClonedPageMetrics[],
      GetCloneMetricsArgs
    >({
      query: ({ organizationId, recordingId }) => ({
        url: '/api/v1/sessions/breakdown/record/page/',
        method: 'GET',
        params: {
          organization_id: organizationId,
          record_id: recordingId,
        },
      }),
    }),
  }),
});

export const {
  useGetSessionsAnalyticsActivityQuery,
  useLazyGetSessionsAnalyticsActivityQuery,
  useGetSessionsSummaryQuery,
  useLazyGetSessionsSummaryQuery,
  useGetSessionsOpportunitiesQuery,
  useLazyGetSessionsOpportunitiesQuery,
  useGetSessionsPresentersQuery,
  useLazyGetSessionsPresentersQuery,
  useGetSessionsAssetsQuery,
  useGetSessionsToursQuery,
  useLazyGetSessionsToursQuery,
  useLazyGetSessionsAssetsQuery,
  useGetDemoSessionsBreakdownQuery,
  useLazyGetDemoSessionsBreakdownQuery,
  useGetTourSessionsBreakdownQuery,
  useLazyGetTourSessionsBreakdownQuery,
  useLazyGetSessionScreenshotsQuery,
  useGetCloneMetricsQuery,
  useGetClonePagesMetricsQuery,
  useLazyGetSessionsCsvQuery,
} = sessionsApi;
